<template>
  <teleport to="body">
    <div class="container-fluid bb-popup-outer">
      <div class="bb-popup-bg d-flex justify-content-center align-items-center">
        <div
          class="card shadow bb-card bb-popup-card mb-2 rounded bb-popup-table-card"
        >
          <div
            class="container-fluid p-4 bb-tooltip-wrapper bb-popup-table-body"
          >
            <div class="bb-rolls-header mb-4">
              <div class="bb-page-title">Party Kills</div>
              <div class="bb-page-arena-name">{{ arenaName }}</div>
            </div>

            <div class="bb-popup-table-controls mb-3">
              <div
                class="bb-roll-summary-row row justify-content-start flex-grow-1 g-3"
              >
                <div
                  class="col-sm-4 col-lg-2 form-group bb-overall-summary-field"
                >
                  <div class="form-floating">
                    <input
                      readonly
                      disabled
                      class="form-control text-center"
                      :value="overallKills"
                      id="party-kills-overall-total"
                    />
                    <label
                      class="bb-float-label optional"
                      for="party-kills-overall-total"
                      >Total Kills</label
                    >
                  </div>
                </div>
                <div class="col-auto ms-auto">
                  <div class="bb-popup-table-actions">
                    <div
                      class="btn-group btn-group-lg flex-wrap"
                      role="group"
                    >
                      <button
                        type="button"
                        class="btn btn-lg btn-primary"
                        :disabled="isSavingUpdates"
                        @click="handleAddRow"
                      >
                        Add
                      </button>
                      <button
                        type="button"
                        class="btn btn-lg btn-warning"
                        :disabled="isSavingUpdates"
                        @click="handleCancel"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        class="btn btn-lg btn-primary"
                        :disabled="
                          isSavingUpdates ||
                          !hasPendingChanges() ||
                          hasIncompleteNewRows()
                        "
                        @click="handleClose"
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="errorMessages.length > 0">
              <div class="row mt-2 mb-3">
                <div class="form-group">
                  <div class="row">
                    <div class="col text-end me-3">
                      <div
                        v-for="errorMessage in errorMessages"
                        :key="errorMessage"
                        class="bb-error-wrap"
                        style="white-space: pre-line"
                      >
                        <div class="row mb-3">
                          {{ errorMessage }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="isLoading"
              class="bb-loader bb-popup-table-loader"
            ></div>

            <div
              v-else-if="killRows.length === 0"
              class="mt-2 mb-3"
            >
              <div class="form-group">
                <div class="row mx-0">
                  <div class="col text-end me-3">
                    <div
                      class="bb-error-wrap"
                      style="white-space: pre-line"
                    >
                      <div class="row mb-3">
                        {{ emptyMessage }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="bb-popup-table-table-wrapper"
            >
              <div class="bb-party-kills-table-content">
                <div
                  v-for="killRow in killRows"
                  :key="killRow.clientKey"
                  class="bb-party-kills-row"
                >
                  <div
                    class="row g-2 align-items-center bb-party-kills-data-row"
                  >
                    <div class="col bb-party-kills-race-col">
                      <div class="form-group">
                        <div class="form-floating">
                          <template v-if="killRow.id">
                            <input
                              readonly
                              disabled
                              class="form-control"
                              :value="killRow.race_name || '-'"
                              :id="`party-kills-race-${killRow.clientKey}`"
                            />
                            <label
                              class="bb-float-label optional"
                              :for="`party-kills-race-${killRow.clientKey}`"
                              >Race</label
                            >
                          </template>
                          <template v-else>
                            <v-select
                              :options="raceOptionsForRow(killRow)"
                              :clearable="true"
                              label="name"
                              :reduce="reduceRace"
                              class="form-control"
                              placeholder="select..."
                              v-model="killRow.race"
                              :class="{
                                'is-invalid': Boolean(
                                  rowErrorsByKey[killRow.clientKey],
                                ),
                              }"
                              :id="`party-kills-race-${killRow.clientKey}`"
                              @click="clearRowError(killRow.clientKey)"
                              @update:modelValue="
                                (value) => onRaceChanged(killRow, value)
                              "
                            >
                              <template #selected-option="{ name }">
                                <span>{{ name || '' }}</span>
                              </template>
                            </v-select>
                            <label
                              class="bb-float-label optional"
                              :for="`party-kills-race-${killRow.clientKey}`"
                              >Race</label
                            >
                          </template>
                        </div>
                      </div>
                    </div>

                    <div class="col-auto bb-party-kills-counter-col">
                      <CounterEmit
                        v-if="killRow.id || killRow.race"
                        :key="`party-kills-count-${killRow.clientKey}`"
                        :pass_ref="buildCounterRef(killRow.clientKey)"
                        :pass_label="'Kills'"
                        :pass_count="Number(killRow.count || 0)"
                        :pass_min="killRow.id ? 0 : 1"
                        :pass_max="killRow.id ? 99 : 999"
                        :pass_digits="2"
                        :disabled="isSavingUpdates"
                        @change="onCountChanged(killRow, $event)"
                      />
                    </div>

                    <div class="col-auto ps-0 d-flex justify-content-start">
                      <button
                        type="button"
                        class="btn btn-primary btn-xl btn-circle d-flex justify-content-center align-items-center bb-party-kills-delete-button"
                        :disabled="isSavingUpdates"
                        @click="handleDeleteRow(killRow)"
                      >
                        <ToolTip
                          text="Delete Party Kill Row"
                          position="left"
                        >
                          <img
                            :src="deleteImg"
                            height="48"
                            width="48"
                            alt="delete image"
                          />
                        </ToolTip>
                      </button>
                    </div>
                  </div>

                  <div
                    v-if="rowErrorsByKey[killRow.clientKey]"
                    class="row mt-2"
                  >
                    <div class="col-12">
                      <div class="bb-error mb-0">
                        <p class="mb-0">
                          {{ rowErrorsByKey[killRow.clientKey] }}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { axios } from '@/common/api.service.js'
import { mapGetters } from 'vuex'

import CounterEmit from '../components/CounterEmit.vue'
import ToolTip from '../components/ToolTip.vue'
import showNotification from '../helpers/showNotification'

import deleteImg from '../assets/images/bbDelete48.png'

export default {
  name: 'PartyKillsView',
  props: {
    fallbackArenaName: {
      type: String,
      default: '',
    },
    fallbackArenaSlug: {
      type: String,
      default: '',
    },
  },
  components: {
    CounterEmit,
    ToolTip,
  },
  data() {
    return {
      deleteImg,
      availableRaces: [],
      isLoading: false,
      isSavingUpdates: false,
      errorMessages: [],
      rowErrorsByKey: {},
      resolvedArenaName: '',
      resolvedArenaSlug: '',
      killRows: [],
      originalValuesById: {},
      pendingDeletedIds: [],
      nextNewRowNumber: 1,
      previousBodyOverflow: '',
      previousHtmlOverflow: '',
    }
  },
  computed: {
    ...mapGetters('auth', {
      getHeaders: 'getHeaders',
    }),
    ...mapGetters('combat', {
      campaign: 'getCampaign',
    }),
    ...mapGetters('characters', {
      currentCharacter: 'getCurrentCharacter',
    }),
    ...mapGetters('races', {
      storedRaces: 'getRaces',
    }),
    hasArenaContext() {
      return Boolean(
        this.resolvedArenaSlug ||
          this.fallbackArenaSlug ||
          this.currentCharacter?.arena_slug ||
          this.currentCharacter?.campaign_slug ||
          this.$route.params.slug ||
          this.$route.query.slug ||
          this.campaign?.slug ||
          this.resolvedArenaName ||
          this.fallbackArenaName ||
          this.currentCharacter?.arena_name ||
          this.campaign?.name,
      )
    },
    arenaSlug() {
      return (
        this.resolvedArenaSlug ||
        this.fallbackArenaSlug ||
        this.currentCharacter?.arena_slug ||
        this.currentCharacter?.campaign_slug ||
        this.$route.params.slug ||
        this.$route.query.slug ||
        this.campaign?.slug ||
        ''
      )
    },
    arenaName() {
      return (
        this.resolvedArenaName ||
        this.fallbackArenaName ||
        this.currentCharacter?.arena_name ||
        this.campaign?.name ||
        'Current Campaign'
      )
    },
    races() {
      if (
        Array.isArray(this.availableRaces) &&
        this.availableRaces.length > 0
      ) {
        console.log('PartyKillsView: using availableRaces', {
          count: this.availableRaces.length,
          sample: this.availableRaces.slice(0, 5).map((race) => ({
            id: race?.id,
            name: race?.name,
          })),
        })
        return this.availableRaces
      }

      const fallbackRaces = Array.isArray(this.storedRaces)
        ? this.storedRaces
        : []
      console.log('PartyKillsView: using storedRaces fallback', {
        count: fallbackRaces.length,
        sample: fallbackRaces.slice(0, 5).map((race) => ({
          id: race?.id,
          name: race?.name,
        })),
      })
      return fallbackRaces
    },
    emptyMessage() {
      if (!this.hasArenaContext) {
        return 'No active combat campaign is loaded.'
      }

      return '*** No Party Kills Currently ***'
    },
    overallKills() {
      return this.killRows.reduce(
        (total, killRow) => total + Number(killRow?.count || 0),
        0,
      )
    },
  },
  mounted() {
    this.lockBackgroundScroll()
    this.loadRaces()
    this.fetchKillStats()
  },
  beforeUnmount() {
    this.restoreBackgroundScroll()
  },
  methods: {
    lockBackgroundScroll() {
      this.previousBodyOverflow = document.body.style.overflow || ''
      this.previousHtmlOverflow = document.documentElement.style.overflow || ''

      document.body.style.overflow = 'hidden'
      document.documentElement.style.overflow = 'hidden'
    },
    restoreBackgroundScroll() {
      document.body.style.overflow = this.previousBodyOverflow
      document.documentElement.style.overflow = this.previousHtmlOverflow
    },
    normalizeResults(payload) {
      if (Array.isArray(payload)) {
        return payload
      }

      if (Array.isArray(payload?.results)) {
        return payload.results
      }

      return []
    },
    buildCounterRef(clientKey) {
      return `party-kills-count-${clientKey}`
    },
    reduceRace(race) {
      return race?.id ?? null
    },
    getRaceLabel(race) {
      return String(race?.name || '')
    },
    buildOriginalValuesById(rows) {
      return (rows || []).reduce((result, killRow) => {
        const rowId = Number(killRow?.id)
        if (!rowId) {
          return result
        }

        result[rowId] = {
          count: Number(killRow?.count || 0),
          race: killRow?.race ?? null,
          updated_at: killRow?.updated_at || '',
        }

        return result
      }, {})
    },
    buildExistingRow(payloadRow) {
      return {
        clientKey: `existing-${payloadRow.id}`,
        id: payloadRow.id,
        race: payloadRow.race,
        race_name: payloadRow.race_name || '',
        count: Number(payloadRow.count || 0),
        updated_at: payloadRow.updated_at || '',
      }
    },
    buildNewRow() {
      const nextRow = {
        clientKey: `new-${this.nextNewRowNumber}`,
        id: null,
        race: null,
        race_name: '',
        count: 0,
      }

      this.nextNewRowNumber += 1
      return nextRow
    },
    updateRow(clientKey, updates) {
      this.killRows = this.killRows.map((killRow) => {
        if (killRow.clientKey !== clientKey) {
          return killRow
        }

        return {
          ...killRow,
          ...updates,
        }
      })
    },
    clearRowError(clientKey) {
      if (!this.rowErrorsByKey[clientKey]) {
        return
      }

      const nextErrors = { ...this.rowErrorsByKey }
      delete nextErrors[clientKey]
      this.rowErrorsByKey = nextErrors
    },
    raceOptionsForRow(currentRow) {
      const usedRaceIds = new Set(
        this.killRows
          .filter((killRow) => killRow.clientKey !== currentRow.clientKey)
          .map((killRow) => killRow.race)
          .filter(Boolean),
      )

      const options = this.races.filter(
        (race) => race?.id === currentRow.race || !usedRaceIds.has(race?.id),
      )

      console.log('PartyKillsView: raceOptionsForRow', {
        clientKey: currentRow?.clientKey,
        currentRace: currentRow?.race,
        totalRaces: this.races.length,
        usedRaceIds: Array.from(usedRaceIds),
        optionCount: options.length,
        options: options.slice(0, 10).map((race) => ({
          id: race?.id,
          name: race?.name,
        })),
      })

      return options
    },
    selectedRaceName(raceId) {
      const race = this.races.find((entry) => entry?.id === raceId)
      return race?.name || ''
    },
    selectedRaceOption(killRow) {
      if (!killRow?.race) {
        return null
      }

      return (
        this.races.find((entry) => entry?.id === killRow.race) || {
          id: killRow.race,
          name: killRow.race_name || '',
        }
      )
    },
    async loadRaces() {
      try {
        console.log('PartyKillsView: loadRaces start')
        await this.$store.dispatch('races/loadStaticRaces')
        console.log('PartyKillsView: store races loaded', {
          storedRaceCount: Array.isArray(this.storedRaces)
            ? this.storedRaces.length
            : 0,
        })

        const response = await axios.get('api/v1/races/?limit=1000', {
          headers: this.getHeaders || {},
          mode: 'same-origin',
        })

        this.availableRaces = Array.isArray(response?.data?.results)
          ? response.data.results
          : []

        console.log('PartyKillsView: direct race fetch complete', {
          responseCount: this.availableRaces.length,
          sample: this.availableRaces.slice(0, 10).map((race) => ({
            id: race?.id,
            name: race?.name,
          })),
        })
      } catch (error) {
        console.error('PartyKillsView: loadRaces failed', error)
        const message = this.extractErrorMessage(error)
        this.errorMessages = [window.bbUiErrorMsg(message)]
      }
    },
    async resolveArenaSlug() {
      const directSlug =
        this.fallbackArenaSlug ||
        this.currentCharacter?.arena_slug ||
        this.currentCharacter?.campaign_slug ||
        this.$route.params.slug ||
        this.$route.query.slug ||
        this.campaign?.slug ||
        ''

      const directArenaId = String(
        this.$route.query.arena_id ||
          this.currentCharacter?.arena ||
          this.campaign?.id ||
          '',
      ).trim()

      this.resolvedArenaName =
        this.resolvedArenaName ||
        this.fallbackArenaName ||
        this.currentCharacter?.arena_name ||
        this.campaign?.name ||
        ''

      if (directSlug) {
        this.resolvedArenaSlug = directSlug
        return directSlug
      }

      if (!directArenaId) {
        return ''
      }

      const headers = this.getHeaders || {}
      const endpoints = []

      if (this.$route.name === 'myCharactersView') {
        endpoints.push('api/v1/campaigns/member/')
      }

      endpoints.push('api/v1/campaigns/my/')
      endpoints.push('api/v1/campaigns/list/')

      for (const initialEndpoint of endpoints) {
        let nextUrl = initialEndpoint

        while (nextUrl) {
          const response = await axios.get(nextUrl, {
            headers,
            mode: 'same-origin',
          })
          const results = Array.isArray(response?.data?.results)
            ? response.data.results
            : []
          const campaign = results.find(
            (entry) => String(entry?.id || '').trim() === directArenaId,
          )

          if (campaign?.slug) {
            this.resolvedArenaSlug = campaign.slug
            this.resolvedArenaName = campaign.name || this.resolvedArenaName
            return campaign.slug
          }

          nextUrl = response?.data?.next ?? null
        }
      }

      return ''
    },
    hasPendingChanges() {
      if (this.pendingDeletedIds.length > 0) {
        return true
      }

      return this.killRows.some((killRow) => {
        if (!killRow.id) {
          return true
        }

        return (
          Number(killRow.count || 0) !==
          Number(this.originalValuesById?.[killRow.id]?.count || 0)
        )
      })
    },
    hasIncompleteNewRows() {
      return this.killRows.some((killRow) => !killRow.id && !killRow.race)
    },
    onCountChanged(killRow, eventPayload) {
      const nextValue = Number(eventPayload?.count)
      if (Number.isNaN(nextValue)) {
        return
      }

      this.errorMessages = []
      this.updateRow(killRow.clientKey, { count: nextValue })
    },
    onRaceChanged(killRow, selectedRaceId) {
      const raceOption =
        this.races.find((entry) => entry?.id === selectedRaceId) || null
      const raceId = this.reduceRace(raceOption)
      console.log('PartyKillsView: onRaceChanged', {
        clientKey: killRow?.clientKey,
        selectedRaceId: raceId,
        selectedRaceName: this.getRaceLabel(raceOption),
        selectedRaceOption: raceOption,
      })
      this.errorMessages = []
      this.clearRowError(killRow.clientKey)
      const nextCount = raceId
        ? Number(killRow.count || 0) > 0
          ? Number(killRow.count || 0)
          : 1
        : 0
      this.updateRow(killRow.clientKey, {
        race: raceId,
        race_name:
          this.getRaceLabel(raceOption) || this.selectedRaceName(raceId),
        count: nextCount,
      })
    },
    handleAddRow() {
      this.errorMessages = []
      const nextRow = this.buildNewRow()
      console.log('PartyKillsView: handleAddRow', {
        nextRow,
        existingRows: this.killRows.map((killRow) => ({
          clientKey: killRow?.clientKey,
          id: killRow?.id,
          race: killRow?.race,
          race_name: killRow?.race_name,
        })),
        totalAvailableRaces: this.races.length,
      })
      this.killRows = [nextRow, ...this.killRows]
    },
    handleDeleteRow(killRow) {
      this.errorMessages = []
      this.clearRowError(killRow.clientKey)

      if (killRow.id) {
        this.pendingDeletedIds = [
          ...new Set([...this.pendingDeletedIds, killRow.id]),
        ]
      }

      this.killRows = this.killRows.filter(
        (existingRow) => existingRow.clientKey !== killRow.clientKey,
      )
    },
    validateRows() {
      const nextRowErrors = {}
      const raceUsage = this.killRows.reduce((result, killRow) => {
        if (!killRow.race) {
          return result
        }

        result[killRow.race] = (result[killRow.race] || 0) + 1
        return result
      }, {})

      this.killRows.forEach((killRow) => {
        if (!killRow.race) {
          nextRowErrors[killRow.clientKey] = 'Select a race.'
          return
        }

        if (raceUsage[killRow.race] > 1) {
          nextRowErrors[killRow.clientKey] = 'Race already has a kill row.'
        }
      })

      this.rowErrorsByKey = nextRowErrors

      if (Object.keys(nextRowErrors).length > 0) {
        this.errorMessages = [
          window.bbUiErrorMsg('Resolve the highlighted party kill rows.'),
        ]
        return false
      }

      return true
    },
    extractErrorMessage(error) {
      const payload = error?.response?.data

      if (typeof payload?.detail === 'string' && payload.detail) {
        return payload.detail
      }

      if (payload && typeof payload === 'object') {
        const firstValue = Object.values(payload)[0]
        if (Array.isArray(firstValue) && firstValue.length > 0) {
          return String(firstValue[0])
        }

        if (typeof firstValue === 'string' && firstValue) {
          return firstValue
        }
      }

      return 'Unable to update party kill statistics.'
    },
    async savePendingChanges() {
      if (!this.validateRows()) {
        return false
      }

      const arenaSlug = await this.resolveArenaSlug()
      if (!arenaSlug) {
        this.errorMessages = [
          window.bbUiErrorMsg('No active combat campaign is loaded.'),
        ]
        return false
      }

      const deletedIds = [...this.pendingDeletedIds]
      const newRows = this.killRows.filter((killRow) => !killRow.id)
      const updatedRows = this.killRows.filter((killRow) => {
        if (!killRow.id) {
          return false
        }

        return (
          Number(killRow.count || 0) !==
          Number(this.originalValuesById?.[killRow.id]?.count || 0)
        )
      })

      this.isSavingUpdates = true
      this.errorMessages = []

      try {
        for (const statId of deletedIds) {
          await axios.delete(
            `api/v1/campaigns/${arenaSlug}/party-kill-stats/${statId}/`,
            {
              data: {
                updated_at: this.originalValuesById?.[statId]?.updated_at || '',
              },
            },
          )
        }

        for (const killRow of newRows) {
          await axios.post(`api/v1/campaigns/${arenaSlug}/party-kill-stats/`, {
            race: killRow.race,
            count: Number(killRow.count || 0),
          })
        }

        for (const killRow of updatedRows) {
          await axios.patch(
            `api/v1/campaigns/${arenaSlug}/party-kill-stats/${killRow.id}/`,
            {
              count: Number(killRow.count || 0),
              updated_at:
                this.originalValuesById?.[killRow.id]?.updated_at || '',
            },
          )
        }

        showNotification('Party kills updated.')
        return true
      } catch (error) {
        const message = this.extractErrorMessage(error)
        this.errorMessages = [window.bbUiErrorMsg(message)]
        return false
      } finally {
        this.isSavingUpdates = false
      }
    },
    async fetchKillStats() {
      this.isLoading = true
      this.errorMessages = []

      const arenaSlug = await this.resolveArenaSlug()

      if (!arenaSlug) {
        this.killRows = []
        this.isLoading = false
        return
      }

      try {
        const response = await axios.get(
          `api/v1/campaigns/${arenaSlug}/party-kill-stats/`,
        )

        this.killRows = this.normalizeResults(response.data).map(
          this.buildExistingRow,
        )
        this.originalValuesById = this.buildOriginalValuesById(this.killRows)
        this.pendingDeletedIds = []
        this.rowErrorsByKey = {}
      } catch (error) {
        this.killRows = []
        this.originalValuesById = {}
        const message = this.extractErrorMessage(error)
        this.errorMessages = [window.bbUiErrorMsg(message)]
      } finally {
        this.isLoading = false
      }
    },
    handleCancel() {
      this.errorMessages = []
      this.rowErrorsByKey = {}
      this.closeDialog()
    },
    async handleClose() {
      if (this.hasPendingChanges()) {
        const isSaved = await this.savePendingChanges()
        if (!isSaved) {
          return
        }
      }

      this.closeDialog()
    },
    closeDialog() {
      if (this.$route.name) {
        const query = { ...this.$route.query }
        delete query.popup

        if (
          this.$route.name === 'myCharactersView' ||
          this.$route.name === 'combatCharacters'
        ) {
          delete query.slug
        }

        this.$router.replace({
          name: this.$route.name,
          params: { ...this.$route.params },
          query,
        })
        return
      }

      this.$router.replace({ name: 'campaignsView' })
    },
  },
}
</script>

<style scoped>
.bb-popup-outer {
  padding-left: 0 !important;
  padding-right: 1.5rem !important;
  box-sizing: border-box;
}

.bb-popup-table-card {
  width: 37%;
  height: min(97vh, 76rem);
  max-height: min(97vh, 76rem);
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  margin-left: auto;
  margin-right: 0;
}

.bb-popup-table-body {
  height: 100%;
  padding: 2rem 2rem 2rem !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.bb-rolls-header {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 2.5rem;
  flex-wrap: wrap;
}

.bb-popup-table-controls {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.bb-roll-summary-row {
  margin-right: 0;
  flex: 1 1 auto;
}

.bb-popup-table-actions {
  display: flex;
  justify-content: flex-end;
  flex: 0 0 auto;
  padding-bottom: 0.2rem;
}

.bb-popup-table-actions .btn {
  width: auto;
  min-width: 7rem;
  margin-bottom: 0;
}

.bb-party-kills-row {
  padding: 0.25rem 0;
}

.bb-party-kills-row:last-child {
  padding-bottom: 1rem;
}

.bb-party-kills-race-col {
  flex: 1 1 0;
  min-width: 0;
  max-width: calc(100% - 19rem);
}

.bb-party-kills-data-row {
  --bs-gutter-x: 0;
  flex-wrap: nowrap;
  column-gap: 1.5rem;
}

.bb-party-kills-counter-col {
  flex: 0 0 14.6rem;
  width: 14.6rem;
  min-width: 14.6rem;
}

.bb-party-kills-delete-button {
  margin-left: 0.35rem;
}

.bb-popup-table-table-wrapper {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1 1 auto;
  min-height: 0;
  padding-right: 2.25rem;
  padding-bottom: 1rem;
  overscroll-behavior: contain;
}

.bb-party-kills-table-content {
  width: 100%;
  flex: 0 0 auto;
}
@media (max-width: 1199.98px) {
  .bb-popup-table-card {
    width: 53%;
  }
}

@media (max-width: 767.98px) {
  .bb-popup-table-card {
    width: calc(100vw - 1.5rem);
  }

  .bb-rolls-header {
    gap: 0.75rem;
  }

  .bb-popup-table-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .bb-popup-table-actions {
    justify-content: flex-start;
  }
}
</style>
